/**
 * Maximum safe length for a mailto URL.
 * Most email clients and browsers silently truncate or reject URLs beyond ~2000 chars.
 */
const MAILTO_MAX_LENGTH = 2000;

type GenerateMailToLinkOptions = {
  readonly body?: string;
  readonly emailAddress?: string;
  readonly subject?: string;
};

/**
 * Returns the trimmed string if value is a non-empty string, otherwise undefined.
 * Guards against non-string runtime values (numbers, objects, null, etc.).
 */
function toTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Builds a RFC 2368-compliant mailto URL with optional subject and body.
 *
 * - Trims and encodes all string fields.
 * - Skips blank fields and non-string values rather than emitting empty parameters.
 * - Logs a warning when the generated URL exceeds the safe browser limit of 2000 chars.
 *
 * @param options - Optional recipient address, subject, and body.
 * @returns A mailto URL string.
 */
export function generateMailToLink({
  body,
  emailAddress,
  subject,
}: GenerateMailToLinkOptions): string {
  const address = toTrimmedString(emailAddress) ?? '';
  const mailto = `mailto:${address}`;

  const params: string[] = [];

  const subjectTrimmed = toTrimmedString(subject);
  if (subjectTrimmed) {
    params.push(`subject=${encodeURIComponent(subjectTrimmed)}`);
  }

  const bodyTrimmed = toTrimmedString(body);
  if (bodyTrimmed) {
    params.push(`body=${encodeURIComponent(bodyTrimmed)}`);
  }

  const url = params.length > 0 ? `${mailto}?${params.join('&')}` : mailto;

  return url.slice(0, MAILTO_MAX_LENGTH);
}
